import {Box, Grid, useMediaQuery, useTheme} from "@mui/material";
import {useAppSelector} from "../../../Config/Hooks";
import {RootState} from "../../../Config/Store";
import React, { useState } from "react";

import TotalizerAlarm from "./TotalizerAlarm/TotalizerAlarm";
import AlarmPage from "./Alarms/AlarmPage";
import { TotalizerType } from "../Overview/DashboardOverviewAlarmsPage";
import { ASectionTitle } from "@atiautomacao/ati-ui-library";
import { LoadingButton } from "@mui/lab";
import SideBar from "../Alarms/Components/sideBarFilterAlarms";
import { ADataGridFilter } from "@atiautomacao/ati-ui";
import {ChipFilterAlarms} from "../Alarms/Components/ChipFilterAlarms";
import { Storage } from "../../../Utils/StorageUtils";
import { ALARM_ADVANCED_FILTERS_KEY } from "../Alarms/Components/ChipFilterAlarms/types";

const ALARM_FILTERS_COUNT_KEY = 'alarm_filters_count';
const TOTALIZER_ALARM_FILTER_KEY = 'totalizer_alarm_filter';

const getInitialFiltersCount = (): number => {
    const stored = Number(Storage.local.get(ALARM_FILTERS_COUNT_KEY, 0));
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
};

const computeInitialAdvancedFilters = (
    powerStationId: number | undefined,
    totalizer: TotalizerType
): ADataGridFilter[] => {
    const stored = Storage.local.get(ALARM_ADVANCED_FILTERS_KEY, null);
    const base: ADataGridFilter[] = (stored && Array.isArray(stored))
        ? (stored as ADataGridFilter[]).filter(f => f.name !== 'powerStationId' && f.name !== 'totalizerType')
        : [];
    if (!powerStationId) return base;
    base.push({ name: 'powerStationId', condition: 'equals', value: powerStationId.toString(), sort: 'none' });
    if (totalizer !== 'none') {
        base.push({ name: 'totalizerType', condition: 'equals', value: totalizer, sort: 'none' });
    }
    return base;
};

export default function DashboardPowerPlantAlarmsPage(){
    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));

    const {selectedPowerPlant} = useAppSelector((state: RootState) => state.dashboard);
    const [sideBarOpen, setSideBarOpen] = useState(false);
    const [totalizerFilter, setTotalizerFilter] = useState<TotalizerType>(localStorage.getItem(TOTALIZER_ALARM_FILTER_KEY) as TotalizerType ?? "none");
    const [advancedFilters, setAdvancedFilters] = useState<ADataGridFilter[]>(
        () => computeInitialAdvancedFilters(selectedPowerPlant?.id, localStorage.getItem(TOTALIZER_ALARM_FILTER_KEY) as TotalizerType ?? "none")
    );
    const [isClearFilters, setIsClearFilters] = useState<boolean>(false);
    const [appliedFiltersCount, setAppliedFiltersCount] = useState<number>(getInitialFiltersCount);
    

    const mergeFiltersWithTotalizerAndPowerStation = (
        sidebarFilters: ADataGridFilter[],
        totalizer: TotalizerType,
        powerStationId: number
    ): ADataGridFilter[] => {
        // Remove filtros anteriores de totalizer e powerStationId para evitar duplicatas
        const baseFilters = sidebarFilters.filter(
            f => f.name !== 'totalizerType' && f.name !== 'powerStationId'
        );

        baseFilters.push({
            name: 'powerStationId',
            condition: 'equals',
            value: powerStationId.toString(),
            sort: 'none'
        });

        if (totalizer !== 'none') {
            baseFilters.push({
                name: 'totalizerType',
                condition: 'equals',
                value: totalizer,
                sort: 'none'
            });
        }

        return baseFilters;
    };

    const handleTotalizerChange = (totalizer: TotalizerType) => {
        if (!selectedPowerPlant) return;

        localStorage.setItem(TOTALIZER_ALARM_FILTER_KEY, totalizer);
        setTotalizerFilter(totalizer);
        setAdvancedFilters(prev =>
            mergeFiltersWithTotalizerAndPowerStation(prev, totalizer, selectedPowerPlant.id)
        );
        setIsClearFilters(false);
    };

    const handleApplySideBarFilters = (sidebarFilters: ADataGridFilter[], filtersCount: number) => {
        if (!selectedPowerPlant) return;

        const merged = mergeFiltersWithTotalizerAndPowerStation(sidebarFilters, totalizerFilter, selectedPowerPlant.id);
        setAdvancedFilters(merged);
        Storage.local.set(ALARM_ADVANCED_FILTERS_KEY, merged.filter(f => f.name !== 'powerStationId' && f.name !== 'totalizerType'));
        setSideBarOpen(false);
        setAppliedFiltersCount(filtersCount);
        Storage.local.set(ALARM_FILTERS_COUNT_KEY, filtersCount);
        setIsClearFilters(false);
    };

    const handleClearFilters = (cleared: boolean) => {
        if (cleared) {
            setAdvancedFilters([]);
            setAppliedFiltersCount(0);
            Storage.local.remove(ALARM_ADVANCED_FILTERS_KEY);
            Storage.local.remove(ALARM_FILTERS_COUNT_KEY);
            Storage.local.remove(TOTALIZER_ALARM_FILTER_KEY);
            setIsClearFilters(cleared);
        }
    };

    return(
        <Grid container paddingLeft={'24px'} paddingRight={'24px'} paddingBottom={'24px'}>
            <Grid item xs={12} paddingTop={0}>
                <ASectionTitle
                    titleLabel={"Alarmes"}
                    styleContainer={{padding: '0px'}}
                    styleContainerTitle={{padding: '0px'}}
                    contentChildrenStyle={{display: isXl ? 'none' : 'flex'}}
                    contentChildren={
                        <LoadingButton
                            size="small"
                            loading={false}
                            startIcon={
                                <Box 
                                    sx={{
                                        color: theme.palette.primary.main,
                                        backgroundColor: '#fff',
                                        fontSize: '0.75rem !important',
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center', 
                                    }}
                                >
                                    {appliedFiltersCount}
                                </Box>
                            }
                            variant="contained"
                            onClick={() => setSideBarOpen(true)}
                        >
                            Filtros Avançados
                        </LoadingButton>
                    }
                />
            </Grid>
            <Grid item xs={12} paddingTop={'16px'}>
                {selectedPowerPlant &&
                <TotalizerAlarm
                    powerStationId={selectedPowerPlant.id}
                    onClickTotalizer={handleTotalizerChange}
                    clearFilters={isClearFilters}
                />}
            </Grid>
            <Grid item xs={12} paddingTop={'4px'} sx={{display: isXl ? 'block' : 'none'}}>
                <ChipFilterAlarms onApplyFilters={handleApplySideBarFilters} hasClearFilters={isClearFilters} />
            </Grid>
            <Grid item xs={12} paddingTop={'24px'}>
                {selectedPowerPlant ? 
                    <AlarmPage 
                        powerPlantId={selectedPowerPlant.id} 
                        advancedFilters={advancedFilters} 
                        hasClearFilters={handleClearFilters}
                    /> 
                    : null
                }
            </Grid>
            <SideBar
                open={sideBarOpen}
                onClose={() => setSideBarOpen(false)}
                onApplyFilters={handleApplySideBarFilters}
                hasClearFilters={isClearFilters}
            />
        </Grid>

    );
}
